/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Tabs,
  Tab,
  Divider
} from '@mui/material';
import {
  Star,
  TrendingUp,
  Shield,
  EmojiEvents,
  CheckCircle,
  HourglassEmpty,
  Timeline,
  Gavel
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { getUserChallenges, Challenge, acceptHumanDefense } from '../services/challengeService';
import { getUserCubeContributions, CubeContributions } from '../services/userService';
import { usePusherChannel, usePusherEvent } from '../hooks/usePusher';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import logger from '../utils/logger';

const CubeDashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [cubeContributions, setCubeContributions] = useState<CubeContributions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [acceptingDefense, setAcceptingDefense] = useState<string | null>(null);

  // Subscribe to user's private channel for CUBE notifications
  const userChannel = usePusherChannel(user?.id ? `user-${user.id}` : '');

  useEffect(() => {
    if (isAuthenticated) {
      loadChallenges();
    }
  }, [isAuthenticated]);

  const loadChallenges = async () => {
    try {
      setLoading(true);
      const [challengeData, contributionsData] = await Promise.all([
        getUserChallenges('all'),
        getUserCubeContributions()
      ]);
      setChallenges(challengeData);
      setCubeContributions(contributionsData);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptDefense = async (challengeId: string) => {
    try {
      setAcceptingDefense(challengeId);
      const result = await acceptHumanDefense(challengeId);
      
      // Redirect to the new debate
      window.location.href = `/debate/${result.debateId}`;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to accept defense');
      setAcceptingDefense(null);
    }
  };

  // Handle CUBE assessment completion notifications
  const handleCubeAssessmentComplete = (data: any) => {
    logger.info('🎯 CUBE Assessment Complete:', data);
    
    // Show appropriate toast notification
    if (data.successful) {
      toast.success(`CUBE Challenge Successful! ${data.refunded ? `Refunded ${data.pointsRefunded || 0} points.` : ''}`);
    } else {
      toast('CUBE Challenge Assessment Complete');
    }
    
    // Refresh challenges to show updated status
    loadChallenges();
  };

  // Handle CUBE defense ready notifications
  const handleCubeDefenseReady = (data: any) => {
    logger.info('🤖 CUBE Defense Ready:', data);
    toast.success('Your CUBE challenge AI defense is ready!');
    
    // Refresh challenges to show updated status
    loadChallenges();
  };

  // Handle CUBE defense failure notifications
  const handleCubeDefenseFailed = (data: any) => {
    logger.info('🚨 CUBE Defense Failed:', data);
    toast.error(`AI Defense Failed: ${data.details}`);
    
    if (data.pointsRefunded > 0) {
      toast.success(`Refunded ${data.pointsRefunded} CUBE points`);
    }
    
    // Refresh challenges to show updated status
    loadChallenges();
  };

  // Bind to CUBE-specific Pusher events
  usePusherEvent(userChannel, 'cube_assessment_complete', handleCubeAssessmentComplete);
  usePusherEvent(userChannel, 'cube_defense_ready', handleCubeDefenseReady);
  usePusherEvent(userChannel, 'cube_defense_failed', handleCubeDefenseFailed);

  if (!isAuthenticated) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="info">
          {t('cubeDashboard.pleaseLogin', 'Please log in to view your CUBE dashboard.')}
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  const submittedChallenges = challenges.filter(c => c.relationshipType === 'submitted');
  const receivedChallenges = challenges.filter(c => c.relationshipType === 'received');
  const availableChallenges = challenges.filter(c => c.relationshipType === 'available');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'pending': return 'warning';
      case 'ai_defense': return 'info';
      case 'awaiting_human_defense': return 'secondary';
      case 'human_defense': return 'primary';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle />;
      case 'pending': return <HourglassEmpty />;
      case 'ai_defense': return <Timeline />;
      case 'awaiting_human_defense': return <Shield />;
      case 'human_defense': return <Gavel />;
      default: return <HourglassEmpty />;
    }
  };

  const renderChallengesList = (challengeList: Challenge[], type: 'submitted' | 'received' | 'available') => {
    if (challengeList.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
          No {type} challenges
        </Typography>
      );
    }

    return (
      <List>
        {challengeList.map((challenge) => {
          const isCompleted = challenge.status === 'completed';
          const result = challenge.result || challenge.finalResult;
          const wasSuccessful = result === 'explanation_challenged' || result === 'explanation_updated';
          const debateId = challenge.aiDefenseDebateId || challenge.humanDefenseDebateId;
          
          return (
            <React.Fragment key={challenge._id}>
              <ListItem sx={{ flexDirection: 'column', alignItems: 'stretch' }}>
                <Box sx={{ display: 'flex', width: '100%', mb: 1 }}>
                  <ListItemIcon>
                    {getStatusIcon(challenge.status)}
                  </ListItemIcon>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                      <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>
                        {challenge.topicName || 'Unknown Topic'}
                      </span>
                      <Chip 
                        size="small" 
                        label={challenge.status.replace('_', ' ')}
                        color={getStatusColor(challenge.status) as any}
                      />
                      <Chip 
                        size="small" 
                        label={challenge.challengeType}
                        variant="outlined"
                      />
                      {isCompleted && result && (
                        <Chip 
                          size="small" 
                          label={wasSuccessful ? 'Challenge Successful' : 'Challenge Failed'}
                          color={wasSuccessful ? 'success' : 'error'}
                        />
                      )}
                      {challenge.refunded && (
                        <Chip 
                          size="small" 
                          label="Refunded"
                          color="info"
                          variant="outlined"
                        />
                      )}
                    </Box>
                    
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        display: 'block', 
                        mb: 1, 
                        color: 'text.secondary',
                        lineHeight: 1.6,
                        p: 1.5,
                        bgcolor: 'grey.50',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'grey.200'
                      }}
                    >
                      {challenge.challengeText}
                    </Typography>
                    <span style={{ fontSize: '0.75rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                      {new Date(challenge.createdAt).toLocaleDateString()}
                      {challenge.paymentAmount > 0 && ` • ${challenge.paymentAmount} points`}
                      {challenge.completedAt && ` • Completed ${new Date(challenge.completedAt).toLocaleDateString()}`}
                      {challenge.refunded && ` • Refunded`}
                    </span>
                  </Box>
                  {type === 'available' && challenge.status === 'awaiting_human_defense' && (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handleAcceptDefense(challenge._id)}
                      disabled={acceptingDefense === challenge._id}
                      startIcon={acceptingDefense === challenge._id ? <CircularProgress size={16} /> : <Shield />}
                    >
                      Accept Defense
                    </Button>
                  )}
                </Box>
                
                {/* AI Assessment Results for Completed Challenges */}
                {isCompleted && challenge.aiAssessment && (
                  <Box sx={{ ml: 6, mb: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.light' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <span style={{ marginRight: '8px', fontSize: '1.25rem' }}>🤖</span>
                      <span style={{ fontWeight: 'bold', color: '#1976d2', fontSize: '0.875rem' }}>
                        AI Assessment Results
                      </span>
                      {wasSuccessful && (
                        <Chip 
                          label="Challenge Successful" 
                          color="success" 
                          size="small" 
                          sx={{ ml: 1 }}
                        />
                      )}
                      {!wasSuccessful && (
                        <Chip 
                          label="Explanation Maintained" 
                          color="info" 
                          size="small" 
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Box>
                    <div style={{ marginBottom: '8px', fontWeight: 500, fontSize: '0.875rem', color: 'rgba(0, 0, 0, 0.87)' }}>
                      <strong>Final Decision:</strong> {wasSuccessful ? 'Your challenge was successful!' : 'The original explanation was maintained'}
                    </div>
                    <div style={{ fontStyle: 'italic', lineHeight: 1.5, fontSize: '0.875rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                      <strong>AI Reasoning:</strong> "{challenge.aiAssessment.reasoning}"
                    </div>
                    {challenge.aiAssessment.completedAt && (
                      <div style={{ marginTop: '8px', fontSize: '0.75rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                        Assessment completed: {new Date(challenge.aiAssessment.completedAt).toLocaleString()}
                      </div>
                    )}
                  </Box>
                )}
                
                {/* Action Buttons */}
                {debateId && (
                  <Box sx={{ ml: 6, display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => window.open(`/debate/${debateId}`, '_blank')}
                    >
                      View Challenge Debate
                    </Button>
                    {isCompleted && wasSuccessful && (
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => window.open(`/debate/view/${debateId}`, '_blank')}
                      >
                        Public View
                      </Button>
                    )}
                  </Box>
                )}
              </ListItem>
              <Divider />
            </React.Fragment>
          );
        })}
      </List>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        {t('cubeDashboard.title', 'CUBE Dashboard')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        {t('cubeDashboard.subtitle', 'Monitor your CUBE challenge activity and defend explanations')}
      </Typography>

      {/* Header Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Star color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" component="div">
                {user?.cubePoints || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('cubeDashboard.cubePoints', 'CUBE Points')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingUp color="success" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" component="div">
                {submittedChallenges.filter(c => {
                  const result = c.result || c.finalResult;
                  return result === 'explanation_challenged' || result === 'explanation_updated';
                }).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('cubeDashboard.successfulChallenges', 'Successful Challenges')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Shield color="info" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" component="div">
                {receivedChallenges.filter(c => {
                  const result = c.result || c.finalResult;
                  return result === 'explanation_maintained';
                }).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('cubeDashboard.defensesWon', 'Defenses Won')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <EmojiEvents color="warning" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" component="div">
                {cubeContributions?.activeCubeContributions || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('cubeDashboard.cubeContributions', 'CUBE Contributions')}
              </Typography>
              {cubeContributions && cubeContributions.totalCubeContributions > cubeContributions.activeCubeContributions && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  ({cubeContributions.totalCubeContributions} total)
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Challenge Activity */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Challenge Activity
          </Typography>
          
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 2 }}>
            <Tab label={`Submitted (${submittedChallenges.length})`} />
            <Tab label={`Received (${receivedChallenges.length})`} />
            <Tab label={`Available to Defend (${availableChallenges.length})`} />
          </Tabs>

          {tabValue === 0 && renderChallengesList(submittedChallenges, 'submitted')}
          {tabValue === 1 && renderChallengesList(receivedChallenges, 'received')}
          {tabValue === 2 && renderChallengesList(availableChallenges, 'available')}
        </CardContent>
      </Card>
    </Container>
  );
};

export default CubeDashboardPage; 